import React, { useMemo, useState } from 'react';
import './LocalAIAgents.css';

const AGENTS = [
  {
    id: 'campus_support',
    name: 'Campus Support',
    description: 'Navigation help, FAQ, and quick troubleshooting.',
    suggestions: ['How to register a student?', 'Where is fee analytics?', 'I cannot find staff module'],
  },
  {
    id: 'admissions',
    name: 'Admissions',
    description: 'Admission documents, process steps, and inquiry guidance.',
    suggestions: ['Documents for class 11 admission', 'Admission process steps'],
  },
  {
    id: 'fees',
    name: 'Fees',
    description: 'Fee components and payment guidance.',
    suggestions: ['Estimate total fee for grade 8', 'Payment options available'],
  },
  {
    id: 'academic_planner',
    name: 'Academic Planner',
    description: 'Study planning and exam preparation support.',
    suggestions: ['Create a 5 day study plan for math science english', 'Exam in 10 days preparation tips'],
  },
  {
    id: 'parent_comms',
    name: 'Parent Communication',
    description: 'Draft parent notices and announcement templates.',
    suggestions: ['Draft holiday notice for parents', 'Draft PTM reminder message'],
  },
  {
    id: 'it_helpdesk',
    name: 'IT Helpdesk',
    description: 'Login/access issue triage with immediate steps.',
    suggestions: ['Users cannot login', 'Staff page is blank after submit'],
  },
];

const nowTime = () => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const normalize = (text = '') => text.toLowerCase().trim();

const createReply = (agentId, input) => {
  const q = normalize(input);

  if (!q) {
    return 'Please enter your question.';
  }

  if (agentId === 'campus_support') {
    if (q.includes('register') && q.includes('student')) {
      return 'Go to Users -> Students -> Add. Fill required fields and submit. For bulk upload use Students -> Import.';
    }
    if (q.includes('fee')) {
      return 'Go to Finance -> Fee Planning for setup and Finance -> Fee Analytics for reports.';
    }
    if (q.includes('staff')) {
      return 'Go to Users -> Staff. If you cannot access, verify your role permissions first.';
    }
    return 'I can help with routes, module locations, and common UI flows. Ask about Students, Faculty, Fees, Exams, or Settings.';
  }

  if (agentId === 'admissions') {
    if (q.includes('11') || q.includes('12')) {
      return 'For senior grades, collect: birth certificate, transfer certificate, address proof, parent ID proof, photos, immunization records, and class 10 marksheet.';
    }
    return 'Standard admission flow: inquiry -> document upload -> interaction/test (if needed) -> fee confirmation -> enrollment.';
  }

  if (agentId === 'fees') {
    if (q.includes('estimate') || q.includes('total')) {
      return 'Sample estimate (local): tuition 30000 + annual 8000 + optional transport/hostel. Share grade and options for exact split.';
    }
    return 'Payment options: full payment, quarterly installments, monthly installments. For failures keep transaction ID and raise ticket.';
  }

  if (agentId === 'academic_planner') {
    if (q.includes('study plan') || q.includes('plan')) {
      return '5-day plan template: Day1 concept review, Day2 guided practice, Day3 revision, Day4 mock test, Day5 error analysis and recap.';
    }
    return 'Exam prep quick rule: 40% concepts, 40% practice, 20% revision + mock tests.';
  }

  if (agentId === 'parent_comms') {
    if (q.includes('holiday')) {
      return 'Draft: Dear Parents, School will remain closed on [Date] for [Reason]. Regular classes resume on [Date]. Regards, School Administration.';
    }
    return 'Draft: Dear Parents, This is a reminder for [Event] on [Date/Time]. Please ensure your ward is prepared. Regards, School Administration.';
  }

  if (agentId === 'it_helpdesk') {
    if (q.includes('blank')) {
      return 'Checklist: verify redirect path, confirm route exists, check browser console errors, and confirm user role permissions.';
    }
    if (q.includes('login')) {
      return 'Login triage: verify credentials, reset password, clear cache, and verify role mapping in access settings.';
    }
    return 'Share module name + exact steps + screenshot details. I will produce a concise bug report format.';
  }

  return 'I could not map that request. Please rephrase with module and issue details.';
};

function LocalAIAgents() {
  const [activeAgentId, setActiveAgentId] = useState(AGENTS[0].id);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text: 'Local AI agents are ready. Select an agent and ask a question.',
      time: nowTime(),
    },
  ]);

  const activeAgent = useMemo(
    () => AGENTS.find((agent) => agent.id === activeAgentId) || AGENTS[0],
    [activeAgentId]
  );

  const sendMessage = (messageText = input) => {
    const text = (messageText || '').trim();
    if (!text) return;

    const userMsg = { role: 'user', text, time: nowTime() };
    const aiMsg = { role: 'assistant', text: createReply(activeAgent.id, text), time: nowTime() };

    setMessages((prev) => [...prev, userMsg, aiMsg]);
    setInput('');
  };

  return (
    <div className="local-ai-page">
      <div className="local-ai-header">
        <h2>Local AI Agents</h2>
        <p>Frontend-only agents running inside browser. No backend calls.</p>
      </div>

      <div className="local-ai-grid">
        <aside className="agent-list-card">
          {AGENTS.map((agent) => (
            <button
              key={agent.id}
              type="button"
              className={`agent-list-item ${activeAgentId === agent.id ? 'active' : ''}`}
              onClick={() => setActiveAgentId(agent.id)}
            >
              <span className="agent-name">{agent.name}</span>
              <span className="agent-description">{agent.description}</span>
            </button>
          ))}
        </aside>

        <section className="agent-chat-card">
          <div className="agent-chat-header">
            <h3>{activeAgent.name}</h3>
            <p>{activeAgent.description}</p>
          </div>

          <div className="agent-suggestions">
            {activeAgent.suggestions.map((s) => (
              <button key={s} type="button" onClick={() => sendMessage(s)}>
                {s}
              </button>
            ))}
          </div>

          <div className="agent-chat-body">
            {messages.map((msg, idx) => (
              <div key={`${msg.role}-${idx}`} className={`msg ${msg.role}`}>
                <p>{msg.text}</p>
                <span>{msg.time}</span>
              </div>
            ))}
          </div>

          <div className="agent-chat-input">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={`Ask ${activeAgent.name}...`}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  sendMessage();
                }
              }}
            />
            <button type="button" onClick={() => sendMessage()}>
              Send
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

export default LocalAIAgents;
